import { useSites } from "@inrange/building-manager-api-client";
import {
  getESysName,
  SdmMatchConfig,
  Site,
  SiteElectricalSystem,
} from "@inrange/building-manager-api-client/models-site";
import { fractionalCurrencySymbol } from "@inrange/theme-components/formatting";
import { Card } from "react-bootstrap";
import { Link } from "react-router-dom";
import Loading from "../../Loading";
import { formatNumber2dp } from "./utils";

const SdmAppliedMatches: React.FC<{
  site: Site;
  esys: SiteElectricalSystem;
  esysCount: number;
  setSelectedDeleteOffer: (offer: SdmMatchConfig | undefined) => void;
}> = ({ site, esys, esysCount, setSelectedDeleteOffer }) => {
  const buyMatches = site.sdmMatches
    .filter((match) => match.buyerId === site.id)
    .filter((match) => match.buyerEsysUuid === esys.uuid);
  const sellMatches = site.sdmMatches
    .filter((match) => match.sellerId === site.id)
    .filter(
      (match) =>
        (match.sellerEsysUuid === null && esys.idx === 0) ||
        match.sellerEsysUuid === esys.uuid
    );
  const otherSiteIds = [
    ...buyMatches.map((m) => m.sellerId),
    ...sellMatches.map((m) => m.buyerId),
  ];
  const sites = useSites(otherSiteIds, undefined);

  // Check if the site data is loading
  const anySitesLoading = sites
    .map((site) => site.isLoading)
    .some((bool) => bool);

  if (anySitesLoading) {
    return (
      <Loading height="100px" label={`Loading site data for applied matches`} />
    );
  }

  const sitesById = sites
    .flatMap((siteResult) => siteResult.data!.sites)
    .reduce(
      (acc, site) => {
        acc[site.id] = site;
        return acc;
      },
      {} as { [key: string]: Site }
    );

  const energyFlowAnnual = site.esys[esys.idx]!.energyFlowAnnual!;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        width: "100%",
        gap: "8px",
      }}
    >
      <Card body className="mb-2" style={{ flex: "1" }}>
        <h6
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontWeight: "bold" }}>
            {esysCount > 1
              ? `Energy match ${getESysName(esysCount, esys)} - `
              : ""}
            Sell matches ({sellMatches.length})
          </span>
          <span>
            Excess supply remaining:{" "}
            {(energyFlowAnnual.exported > 0 ||
              (energyFlowAnnual.exported === 0 &&
                energyFlowAnnual.curtailed === 0)) && (
              <>
                {energyFlowAnnual.exported > 0 &&
                  energyFlowAnnual.curtailed > 0 && <br />}
                {formatNumber2dp(energyFlowAnnual.exported)} kWh (Exported)
              </>
            )}
            {energyFlowAnnual.curtailed > 0 && (
              <>
                {energyFlowAnnual.exported > 0 &&
                  energyFlowAnnual.curtailed > 0 && <br />}
                {formatNumber2dp(energyFlowAnnual.curtailed)} kWh (Curtailed)
              </>
            )}
          </span>
        </h6>
        {sellMatches.length === 0 && <div>No sell matches yet.</div>}
        {sellMatches.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            {sellMatches.map((sellMatch) => (
              <div
                key={sellMatch.buyerId}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  {sellMatch.isWired ? "Wired: " : ""}
                  <Link to={`/site/${sellMatch.buyerId}/sdm`}>
                    {sellMatch.buyerName}
                  </Link>
                  {Object.values(sitesById[sellMatch.buyerId].esys).length >
                    1 &&
                    ` [Energy match ${getESysName(
                      Object.values(sitesById[sellMatch.buyerId].esys).length,
                      Object.values(sitesById[sellMatch.buyerId].esys).find(
                        (esys) => esys.uuid === sellMatch.buyerEsysUuid
                      )!
                    )}]`}
                  <br />
                  {formatNumber2dp(sellMatch.volume)} kWh at{" "}
                  {(sellMatch.tariff * 100.0).toFixed(2)}{" "}
                  {fractionalCurrencySymbol(site.currencyCode)}/kWh
                  <br />
                  PPA Type:{" "}
                  {sellMatch.ppaContractType === "sleeved" ? "Sleeved" : "VPPA"}
                  , Length: {sellMatch.ppaLength}, Index: {sellMatch.ppaIndex}
                </div>
                <div>
                  <a
                    onClick={() => setSelectedDeleteOffer(sellMatch)}
                    className="text-danger"
                    style={{
                      cursor: "pointer",
                      textDecoration: "none",
                    }}
                  >
                    Delete match
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card body className="mb-2" style={{ flex: "1" }}>
        <h6
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontWeight: "bold" }}>
            {esysCount > 1
              ? `Energy match ${getESysName(esysCount, esys)} - `
              : ""}
            Buy matches ({buyMatches.length})
          </span>
          <span>
            Unmet demand remaining:{" "}
            {formatNumber2dp(energyFlowAnnual.gridImport)} kWh
          </span>
        </h6>
        {buyMatches.length === 0 && <div>No buy matches yet.</div>}
        {buyMatches.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            {buyMatches.map((buyMatch) => (
              <div
                key={buyMatch.sellerId}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  {buyMatch.isWired ? "Wired: " : ""}
                  <Link to={`/site/${buyMatch.sellerId}/sdm`}>
                    {buyMatch.sellerName}
                  </Link>
                  {Object.values(sitesById[buyMatch.sellerId].esys).length >
                    1 &&
                    ` [Energy match ${getESysName(
                      Object.values(sitesById[buyMatch.sellerId].esys).length,
                      Object.values(sitesById[buyMatch.sellerId].esys).find(
                        (esys) =>
                          esys.uuid === buyMatch.sellerEsysUuid ||
                          (esys.idx === 0 && buyMatch.sellerEsysUuid === null)
                      )!
                    )}]`}
                  <br />
                  {formatNumber2dp(buyMatch.volume)} kWh at{" "}
                  {(buyMatch.tariff * 100.0).toFixed(2)}{" "}
                  {fractionalCurrencySymbol(site.currencyCode)}/kWh
                  <br />
                  PPA Type:{" "}
                  {buyMatch.ppaContractType === "sleeved" ? "Sleeved" : "VPPA"},
                  Length: {buyMatch.ppaLength}, Index: {buyMatch.ppaIndex}
                </div>
                <div>
                  <a
                    onClick={() => setSelectedDeleteOffer(buyMatch)}
                    className="text-danger"
                    style={{
                      cursor: "pointer",
                      textDecoration: "none",
                    }}
                  >
                    Delete match
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default SdmAppliedMatches;
